import hoistStatics from 'hoist-non-react-statics'
import { createClient } from 'mage-core'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { createAction } from 'redux-actions'

import { matchCapabilitiesToProps } from '../utils'
import * as c from '../constants'
import CapabilitiesContext from './CapabilitiesContext'

const replaceKeys = (s, [k, v]) => R.replace(`{{${k}}}`, v, s)

const createUrl = (uri = '', params = {}) => R.reduce(replaceKeys, uri, R.toPairs(params))

export default function withCapabilities (mapCapabilitiesToProps) {
  const capabilitiesFactoryCreator = matchCapabilitiesToProps(mapCapabilitiesToProps)

  return function withCapabilitiesInner (WrappedComponent) {
    const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component'
    const wrappedComponentName = `WithCapabilities(${displayName})`

    const capabilitiesFactory = capabilitiesFactoryCreator({ wrappedComponentName })

    class WithCapabilities extends React.Component {
      constructor (props) {
        super(props)

        this.createRequest = this.createRequest.bind(this)
        this.createAction = this.createAction.bind(this)
        this.getCapability = this.getCapability.bind(this)
      }

      createRequest (cap, contract) {
        const pathParams = R.compose(
          R.pluck('name'),
          R.filter((p) => p.type === 'path')
        )(contract.params)

        return (overrides = {}) => {
          const {
            body,
            params = {},
            ...opts
          } = R.mergeDeepRight(cap.options, overrides)

          const path = R.pick(pathParams, params)
          const query = R.omit(pathParams, params)
          const url = createUrl(contract.uri, path)

          const request = {
            method: contract.method,
            headers: contract.headers,
            url: url,
            params: query,
            data: body
          }

          return this.props.client.request(R.mergeDeepRight(
            request,
            opts
          ))
        }
      }

      createAction (cap, contract) {
        const loading = createAction(cap.LOADING)
        const perform = createAction(
          cap.CAPABILITY,
          this.createRequest(cap, contract)
        )

        return overrides => [
          loading(),
          perform(overrides)
        ]
      }

      getCapability (cap) {
        const contract = this.context[cap.KEY]
        return cap.valid && contract && this.createAction(cap, contract)
      }

      addExtraProps (caps) {
        return bindActionCreators(
          R.compose(
            R.filter(R.identity),
            R.map(this.getCapability)
          )(caps),
          this.props.dispatch
        )
      }

      render () {
        return React.createElement(
          WrappedComponent,
          R.mergeDeepRight(
            this.props,
            this.addExtraProps(capabilitiesFactory(this.props))
          )
        )
      }
    }

    WithCapabilities.propTypes = {
      client: c.ClientShape.isRequired,
      dispatch: PropTypes.func.isRequired
    }

    WithCapabilities.displayName = wrappedComponentName
    WithCapabilities.WrappedComponent = WrappedComponent

    WithCapabilities.contextType = CapabilitiesContext

    return connect(
      state => ({
        client: createClient(state)
      })
    )(hoistStatics(WithCapabilities, WrappedComponent))
  }
}
